import { Form } from "formik";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import * as yup from "yup";

import { addToast, toastMessageTypes } from "@/redux/actions/toaster";
import {
  admin_cse,
  provider_behavioral,
  provider_case_manager,
  provider_peer,
  provider_prescribe,
} from "@/config/roles";
import { api } from "@/APIRequests";
import { Button2 } from "@/components/UI2/atoms/Button";
import { CharacterCounter } from "@/component-library/CharacterCounter/CharacterCounter";
import { CustomForm } from "@/component-library";
import { FormTextArea } from "@/component-library/FormFields/FormTextArea/FormTextArea";
import { hideLoader, showLoader } from "@/redux/actions/loader";
import { isAuthorized } from "@/redux/actions/auth";
import { Menu2 } from "@/components/UI2/molecules/Menu";
import { ModalControlled2 } from "@/components/UI2/molecules/Modal";
import { XSS_REGEX } from "@/constants/security";

export function ReportAcuityLegacy({ patientId }) {
  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch();

  const onModalSubmission = async (reportReason) => {
    dispatch(showLoader(true));
    const response = await api.patient.report_acuity({
      data: {
        patient_id: patientId,
        report_reason: reportReason,
      },
      params: { showLoader: true },
    });

    dispatch(hideLoader());
    closeModal();

    dispatch(
      addToast({
        dismissTimeout: 1000,
        message: response?.message || "Acuity reported successfully",
        messageType: toastMessageTypes.success_v2,
      }),
    );
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const isAuthorizedToReportAcuity = () => {
    return (
      isAuthorized(admin_cse) ||
      isAuthorized(provider_prescribe) ||
      isAuthorized(provider_behavioral) ||
      isAuthorized(provider_peer) ||
      isAuthorized(provider_case_manager)
    );
  };

  return (
    <>
      {isAuthorizedToReportAcuity() && (
        <Menu2
          items={[{ id: "report-acuity", label: "Report Acuity" }]}
          onAction={() => setShowModal(true)}
          placement="bottom right"
        />
      )}

      <ModalControlled2
        className="tw:max-w-80"
        isOpen={showModal}
        onOpenChange={setShowModal}
        title="Report Acuity Score"
      >
        <ReportAcuityForm onClose={closeModal} onModalSubmission={onModalSubmission} />
      </ModalControlled2>
    </>
  );
}

const ReportAcuityForm = (props) => {
  const maxCharacters = 4000;
  const characterCountExceededError = `Limit text to ${maxCharacters} characters`;

  const reportReasonSchema = yup.object().shape({
    reportReason: yup
      .string()
      .required("Report reason is required")
      .max(maxCharacters, characterCountExceededError)
      .test("special characters", "Special characters are not allowed", (value) => {
        return !XSS_REGEX.test(value);
      }),
  });

  const renderForm = (formikProps) => {
    return (
      <Form>
        <div className="tw:flex tw:flex-col tw:gap-5">
          <p className="body-md">
            If this acuity score doesn’t seem right, you can flag it for review. While the score
            won’t change in the platform, your feedback will help us improve how acuity is
            determined in the future.
          </p>

          <p className="body-md tw:mb-5">
            Explain why you believe this score is incorrect, including specific clinical reasons.
          </p>

          <FormTextArea
            formikProps={formikProps}
            inputClassName="ReportAcuity--modal-free-text-input"
            name="reportReason"
            placeholder="Specify..."
            triggerErrorOnChange
          />

          {formikProps.errors?.reportReason === characterCountExceededError && (
            <CharacterCounter
              characterLimit={maxCharacters}
              className="ReportAcuity--input-character-counter"
              length={formikProps.values?.reportReason?.length}
            />
          )}

          <div className="tw:flex tw:gap-4 tw:self-end">
            <Button2 onPress={props.onClose} type="button" variant="secondary">
              Cancel
            </Button2>

            <Button2 isDisabled={!formikProps.values.reportReason} type="submit">
              Report Acuity Score
            </Button2>
          </div>
        </div>
      </Form>
    );
  };

  return (
    <CustomForm
      initialValues={{ reportReason: "" }}
      onSubmit={async (formData) => {
        await props.onModalSubmission(formData.reportReason);
      }}
      render={renderForm}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={reportReasonSchema}
    />
  );
};
